import {Link} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import casimiroSaludo from '@/assets/casimiro-saludo.webp'
import {FreeProColumns} from '@/components/plan-value-items'
import {Reveal} from '@/components/reveal'
import {Tag} from '@/components/ui/tag'
import {cn} from '@/lib/utils'
import {CTA_SELLO, PautaEyebrow, SeccionHead} from './kit'

const STEPS = ['registro', 'autoevaluacion', 'exposicion', 'operacion'] as const
const EN_JUEGO = ['estandar', 'evidencia', 'multa'] as const
const AUDIENCIAS = ['empresa', 'responsable'] as const

function CtaRegistro({testId}: {testId: string}) {
  const {t} = useTranslation()
  return (
    <div className="mt-10">
      <Link to="/registro/empresa" className={CTA_SELLO} data-testid={testId}>
        {t('landing.nav.cta')}
      </Link>
      <p className="mt-2.5 text-xs text-muted-foreground">{t('landing.precios.ctaAyuda')}</p>
    </div>
  )
}

/** Sin cifras: el monto de la multa lo calcula el producto con el UVT del año. */
function EnJuego() {
  const {t} = useTranslation()
  return (
    <section className="bg-background" data-testid="landing-en-juego">
      <div className="mx-auto max-w-6xl px-6 py-20 sm:py-28">
        <SeccionHead
          eyebrow={t('landing.enJuego.eyebrow')}
          title={t('landing.enJuego.title')}
          body={t('landing.enJuego.body')}
        />
        <div className="mt-12 grid gap-4 sm:grid-cols-3">
          {EN_JUEGO.map((key, i) => (
            <Reveal key={key} delayMs={i * 40} className="surface flex flex-col p-5">
              <div className="flex items-center justify-between gap-2">
                <p className="font-mono text-[10px] font-semibold tracking-[0.12em] text-muted-foreground uppercase">
                  {t(`landing.enJuego.${key}.label`)}
                </p>
                {/* `ink` y no `green`: «Gratis» es énfasis de marketing, no un estado (U2(b)). */}
                {key === 'multa' && <Tag tone="ink">{t('landing.enJuego.multa.sello')}</Tag>}
              </div>
              <p className="mt-3 text-sm text-foreground">{t(`landing.enJuego.${key}.body`)}</p>
            </Reveal>
          ))}
        </div>
        <Reveal delayMs={80}>
          <p className="mt-5 max-w-3xl text-xs text-muted-foreground">{t('landing.enJuego.footnote')}</p>
        </Reveal>
      </div>
    </section>
  )
}

function ComoFunciona() {
  const {t} = useTranslation()
  return (
    <section className="bg-sidebar" data-testid="landing-como-funciona">
      <div className="mx-auto max-w-6xl px-6 py-20 sm:py-28">
        <SeccionHead
          eyebrow={t('landing.como.eyebrow')}
          title={t('landing.como.title')}
          body={t('landing.como.body')}
        />
        <ol className="mt-14 grid gap-x-8 gap-y-10 sm:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((key, i) => (
            <Reveal key={key} as="li" delayMs={i * 40} className="relative border-t border-primary/25 pt-6">
              <span aria-hidden="true" className="absolute -top-[3px] left-0 h-1.5 w-1.5 rounded-full bg-primary" />
              <span className="font-mono text-[11px] font-semibold tracking-[0.14em] text-primary">
                {String(i + 1).padStart(2, '0')}
              </span>
              <h3 className="mt-3 font-heading text-lg text-foreground">{t(`landing.como.${key}.title`)}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{t(`landing.como.${key}.body`)}</p>
            </Reveal>
          ))}
        </ol>
      </div>
    </section>
  )
}

/** Reusa el bloque de «Plan y facturación» para que las dos superficies no deriven. */
function QueIncluye() {
  const {t} = useTranslation()
  return (
    <section className="bg-background" data-testid="landing-que-incluye">
      <div className="mx-auto max-w-6xl px-6 py-20 sm:py-28">
        <SeccionHead
          eyebrow={t('landing.incluye.eyebrow')}
          title={t('planFacturacion.value.title')}
          body={t('plans.upsellBody')}
        />
        <Reveal delayMs={60} className="mt-12">
          <FreeProColumns />
        </Reveal>
        <Reveal delayMs={90}>
          <CtaRegistro testId="landing-incluye-cta" />
        </Reveal>
      </div>
    </section>
  )
}

function ParaQuien() {
  const {t} = useTranslation()
  return (
    <section className="bg-sidebar" data-testid="landing-para-quien">
      <div className="mx-auto max-w-6xl px-6 py-20 sm:py-28">
        <SeccionHead eyebrow={t('landing.paraQuien.eyebrow')} title={t('landing.paraQuien.title')} />
        <div className="mt-12 grid gap-4 sm:grid-cols-2">
          {AUDIENCIAS.map((key, i) => (
            <Reveal key={key} delayMs={i * 40} className="surface p-6">
              <h3 className="font-heading text-lg text-foreground">{t(`landing.paraQuien.${key}.title`)}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{t(`landing.paraQuien.${key}.body`)}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  )
}

/** El fondo del render de Casimiro está llevado al ink exacto de la casa, con un matte que
 * sigue al personaje: cambiar el token ink lo recorta en un cuadro. */
function ServicioSst() {
  const {t} = useTranslation()
  return (
    <section
      className="relative overflow-hidden bg-ink-deep text-ink-deep-foreground"
      data-testid="landing-servicio-sst">
      <div aria-hidden="true" className="cover-ruled absolute inset-0" />
      <div className="relative mx-auto grid max-w-6xl items-center gap-10 px-6 py-20 sm:py-28 lg:grid-cols-[1.15fr_0.85fr]">
        <Reveal>
          <PautaEyebrow onInk>{t('landing.servicioSst.eyebrow')}</PautaEyebrow>
          <h2 className="mt-5 max-w-xl font-heading text-3xl tracking-tight text-ink-deep-foreground sm:text-[2.5rem] sm:leading-[1.1]">
            {t('landing.servicioSst.title')}
          </h2>
          <p className="mt-4 max-w-xl text-base text-ink-deep-foreground/75">{t('landing.servicioSst.body')}</p>
          <Link to="/registro/empresa" className={cn(CTA_SELLO, 'mt-9')} data-testid="landing-servicio-sst-cta">
            {t('landing.servicioSst.cta')}
          </Link>
        </Reveal>
        <Reveal delayMs={80} className="flex justify-center lg:justify-end">
          <img
            src={casimiroSaludo}
            alt=""
            width={480}
            height={515}
            loading="lazy"
            decoding="async"
            className="h-auto w-40 max-w-full sm:w-56 lg:w-64"
          />
        </Reveal>
      </div>
    </section>
  )
}

export {ComoFunciona, EnJuego, ParaQuien, QueIncluye, ServicioSst}
